import { ApiError, AuthenticationRequiredError, NetworkError } from "../../api/errors";
import { isMessageKey } from "../i18n/translate";
import type { Translate } from "../i18n/translate";

/**
 * Cause of an error, used to choose a hint and whether a retry can help (UX-108).
 *
 * - `offline`: the server was not reached.
 * - `authentication`: the sign-in is missing or expired.
 * - `permission`: the server refused the request (403).
 * - `request`: the server refused the content of the request (other 4xx).
 * - `server`: the server or a service behind it failed (5xx, also 429).
 * - `unknown`: no API error (for example a programming error).
 */
export type ErrorCause =
  "offline" | "authentication" | "permission" | "request" | "server" | "unknown";

/** A user-facing explanation of an error. */
export interface ErrorExplanation {
  /** Translated message; it never contains server text (UX-107) and never the error code. */
  readonly message: string;
  /** Error code (`TT-xxxx`, or `HTTP <status>` without code) for support, or `null`. */
  readonly code: string | null;
  /** Cause of the error. */
  readonly cause: ErrorCause;
  /** Translated hint on what to do next for generic messages, or `null`. */
  readonly hint: string | null;
}

/**
 * Codes of `docs/error-codes.md` that deliberately get the generic message plus the code: the PWA
 * cannot trigger them (operator, background job, provisioning and webhook endpoints, the contact form
 * of the website TT-3700, TT-3701, TT-5011, TT-5012 of F-121), the server no
 * longer returns them (TT-3282 since F-97) or they mean a programming error. Every other documented
 * code has a message `error.<code>` (checked by a test).
 */
export const genericErrorCodes: ReadonlySet<string> = new Set([
  "TT-1011",
  "TT-1082",
  "TT-2004",
  "TT-2016",
  "TT-3022",
  "TT-3031",
  "TT-3032",
  "TT-3054",
  "TT-3056",
  "TT-3063",
  "TT-3282",
  "TT-3640",
  "TT-3700",
  "TT-3701",
  "TT-4001",
  "TT-4002",
  "TT-4003",
  "TT-5011",
  "TT-5012",
]);

/** Code of a missing access right; its message gets the permission hint like an unknown 403. */
const accessRightMissingCode = "TT-3004";

/**
 * Determines the cause of an HTTP status.
 *
 * @param status - HTTP status code.
 * @returns The cause.
 */
function causeOfStatus(status: number): ErrorCause {
  let cause: ErrorCause;

  if (status === 401) {
    cause = "authentication";
  } else if (status === 403) {
    cause = "permission";
  } else if (status === 429 || status >= 500) {
    cause = "server";
  } else {
    cause = "request";
  }

  return cause;
}

/**
 * Returns the translated message of an error code whose text depends on the platform or that is
 * shared with an older key, or `null`.
 *
 * @param code - The error code.
 * @param t - Translation function.
 * @param purchasePaths - Whether ways to buy may be shown.
 * @returns The message or `null`.
 */
function specialMessage(code: string, t: Translate, purchasePaths: boolean): string | null {
  let message: string | null;

  if (code === "TT-3001" || code === "TT-3002") {
    message = t("error.notRegistered");
  } else if (code === accessRightMissingCode) {
    message = t("error.forbidden");
  } else if (code === "TT-3034" || code === "TT-3062") {
    message = t(purchasePaths ? "error.entitlementMissing" : "error.functionNotEnabled");
  } else if (code === "TT-3026") {
    message = t(purchasePaths ? "error.administrationReadOnly" : "error.readOnly");
  } else if (code === "TT-3036") {
    message = t("error.projectTimeTrackingDisabled");
  } else if (code === "TT-3045") {
    message = t("error.managersMayNotCreateProjects");
  } else {
    message = null;
  }

  return message;
}

/**
 * Returns the translated message of an error code (`error.TT-xxxx`), or `null` for an unknown code
 * and the codes that deliberately get the generic message.
 *
 * @param code - The error code.
 * @param t - Translation function.
 * @param purchasePaths - Whether ways to buy may be shown; `false` in the native apps.
 * @returns The message or `null`.
 */
export function messageForErrorCode(
  code: string,
  t: Translate,
  purchasePaths = true,
): string | null {
  const key = `error.${code}`;
  const message = specialMessage(code, t, purchasePaths) ?? (isMessageKey(key) ? t(key) : null);

  return message;
}

/**
 * Explains an API error by its code; server text (`detail`, English) is never shown (UX-107).
 *
 * @param error - The API error.
 * @param t - Translation function.
 * @param purchasePaths - Whether ways to buy may be shown.
 * @returns The explanation.
 */
function explainApiError(error: ApiError, t: Translate, purchasePaths: boolean): ErrorExplanation {
  const cause = causeOfStatus(error.status);
  const code = error.code ?? `HTTP ${String(error.status)}`;
  const specific = error.code === null ? null : messageForErrorCode(error.code, t, purchasePaths);
  let message: string;
  let hint: string | null = null;

  if (specific !== null) {
    message = specific;
    hint = error.code === accessRightMissingCode ? t("error.hint.permission") : null;
  } else if (cause === "permission") {
    message = t("error.forbidden");
    hint = t("error.hint.permission");
  } else if (cause === "server") {
    message = t("error.generic");
    hint = t("error.hint.server");
  } else {
    message = t("error.generic");
  }

  const explanation: ErrorExplanation = { message, code, cause, hint };

  return explanation;
}

/**
 * Converts an error into a user-facing explanation: a translated message by error code, the code
 * for support and, for generic messages, a hint on what to do next (UX-107, UX-108). No personal
 * data and no server text is shown. Where no way to buy may be shown (the native apps, ADR-0087),
 * a missing entitlement is only "not enabled for the organization" without a plan hint, and the
 * read-only administration is named without its cause.
 *
 * @param error - The error.
 * @param t - Translation function.
 * @param purchasePaths - Whether ways to buy may be shown; `false` in the native apps.
 * @returns The explanation.
 */
export function explainError(error: unknown, t: Translate, purchasePaths = true): ErrorExplanation {
  let explanation: ErrorExplanation;

  if (error instanceof NetworkError) {
    explanation = {
      message: t("error.network"),
      code: null,
      cause: "offline",
      hint: t("error.hint.offline"),
    };
  } else if (error instanceof AuthenticationRequiredError) {
    explanation = {
      message: t("auth.redirecting"),
      code: null,
      cause: "authentication",
      hint: null,
    };
  } else if (error instanceof ApiError) {
    explanation = explainApiError(error, t, purchasePaths);
  } else {
    explanation = { message: t("error.title"), code: null, cause: "unknown", hint: null };
  }

  return explanation;
}

/**
 * Converts an error into one line of text: the translated message and, if known, the error code
 * (`Die Anfrage ist fehlgeschlagen. (Fehlercode TT-9999)`).
 *
 * @param error - The error.
 * @param t - Translation function.
 * @param purchasePaths - Whether ways to buy may be shown; `false` in the native apps.
 * @returns The message.
 */
export function describeError(error: unknown, t: Translate, purchasePaths = true): string {
  const { message, code } = explainError(error, t, purchasePaths);
  const described = code === null ? message : t("error.withCode", { message, code });

  return described;
}

/**
 * Describes an error code without an error object, for example the code a rejected offline clock
 * event carries: the translated message, or the generic message with the code.
 *
 * @param code - The error code, or `null` if none is known.
 * @param t - Translation function.
 * @param purchasePaths - Whether ways to buy may be shown; `false` in the native apps.
 * @returns The message.
 */
export function describeErrorCode(code: string | null, t: Translate, purchasePaths = true): string {
  const specific = code === null ? null : messageForErrorCode(code, t, purchasePaths);
  let described: string;

  if (specific !== null) {
    described = specific;
  } else if (code === null) {
    described = t("error.generic");
  } else {
    described = t("error.withCode", { message: t("error.generic"), code });
  }

  return described;
}
